import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import inventoryService from '../services/inventoryService';
import cardRatesService from '../services/cardRatesService';
import CurrencyInput from './CurrencyInput';
import ProjectInitialBalances from './ProjectInitialBalances';
import {
  Folder, X, Plus, DollarSign, Calendar, Clock, CheckCircle2,
  TrendingUp, AlertCircle, ChevronRight, ChevronLeft, Phone, Mail,
  MapPin, FileText, ExternalLink, CreditCard, Layers, Tag,
  Calculator, RefreshCw, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const ClientFileModal = ({ client, onClose, initialTab = 'active' }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(initialTab); // 'active' | 'completed' | 'all'
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [contractProject, setContractProject] = useState(null); // Projeto selecionado para ver/ajustar contrato

  // Formulário do novo projeto com contrato
  const getInitialFormData = () => ({
    project_name: '',
    description: '',
    total_amount: '',
    reference_date: new Date().toISOString().split('T')[0],
    has_entry: false,
    entry_amount: '',
    entry_date: new Date().toISOString().split('T')[0],
    entry_payment_type: 'pix',
    entry_splits_count: 1,
    entry_splits: [],
    entry_supplier_id: '',
    installments: 1,
    first_installment_date: (() => {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      return d.toISOString().split('T')[0];
    })(),
    payment_type: 'cash',
    rate_paid_by: 'cliente',
    card_brand: '',
    installment_splits: []
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Rótulo amigável em português para formas de pagamento
  const getPaymentMethodLabel = (type) => {
    const map = {
      cash: 'Dinheiro',
      pix: 'PIX',
      pix_direct: 'PIX direto ao fornecedor',
      card: 'Cartão',
      credit_card: 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      bank_transfer: 'Transferência Bancária',
      cheque: 'Cheque',
      cheque_direct: 'Cheque direto ao fornecedor',
      boleto: 'Boleto'
    };
    return map[type] || String(type || '').toUpperCase();
  };

  // Buscar taxas de cartão
  const { data: cardRatesData } = useQuery({
    queryKey: ['card-rates'],
    queryFn: cardRatesService.getRates,
    staleTime: 10 * 60 * 1000
  });
  const cardRates = cardRatesData?.data || [];

  // Buscar fornecedores para pagamentos diretos
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers'],
    queryFn: inventoryService.getSuppliers,
    staleTime: 10 * 60 * 1000
  });
  const suppliers = suppliersData?.data || [];

  // Fracionamento da entrada
  const handleEntrySplitsCountChange = (count) => {
    const numSplits = parseInt(count, 10) || 1;
    const totalEntry = parseFloat(formData.entry_amount) || 0;
    const splitBaseAmount = numSplits > 0 ? parseFloat((totalEntry / numSplits).toFixed(2)) : 0;

    let newSplits = [];
    let accumulated = 0;
    for (let i = 0; i < numSplits; i++) {
      const [year, month, day] = (formData.entry_date || new Date().toISOString().split('T')[0]).split('-').map(Number);
      const d = new Date(year, month - 1, day);
      d.setMonth(d.getMonth() + i);
      const dateStr = d.toISOString().split('T')[0];

      let amount;
      if (i === numSplits - 1) {
        amount = parseFloat((totalEntry - accumulated).toFixed(2));
      } else {
        amount = splitBaseAmount;
        accumulated = parseFloat((accumulated + amount).toFixed(2));
      }

      newSplits.push({
        amount: amount > 0 ? amount : '',
        date: dateStr,
        payment_type: formData.entry_payment_type || 'pix'
      });
    }

    setFormData(prev => ({
      ...prev,
      entry_splits_count: numSplits,
      entry_splits: newSplits
    }));
  };

  const handleEntrySplitChange = (index, field, value) => {
    setFormData(prev => {
      const newSplits = [...(prev.entry_splits || [])];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { ...prev, entry_splits: newSplits };
    });
  };

  // Buscar projetos do cliente
  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['client-projects', client.id],
    queryFn: async () => {
      const response = await projectsService.getProjectsByClientId(client.id, { limit: 100 });
      return response;
    }
  });

  const allProjects = useMemo(() => {
    if (!projectsData?.data) return [];
    const data = projectsData.data;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  }, [projectsData]);

  // Filtrar projetos por aba
  const filteredProjects = useMemo(() => {
    if (tab === 'active') {
      return allProjects.filter(p => p.status !== 'COMPLETED' && p.status !== 'DELIVERED' && p.status !== 'completed' && p.status !== 'delivered');
    }
    if (tab === 'completed') {
      return allProjects.filter(p => p.status === 'COMPLETED' || p.status === 'DELIVERED' || p.status === 'completed' || p.status === 'delivered');
    }
    return allProjects;
  }, [allProjects, tab]);

  // KPIs consolidados de relacionamento com o cliente
  const clientStats = useMemo(() => {
    let totalContracted = 0;
    let totalReceived = 0;

    allProjects.forEach(p => {
      const val = parseFloat(p.project_value || p.estimated_budget || 0);
      const paid = parseFloat(p.total_received || 0);
      totalContracted += val;
      totalReceived += paid;
    });

    const pending = Math.max(0, totalContracted - totalReceived);

    return {
      projectsCount: allProjects.length,
      totalContracted,
      totalReceived,
      pending
    };
  }, [allProjects]);

  // Mutation para criar projeto com contrato
  const createProjectWithContractMutation = useMutation({
    mutationFn: (data) => projectsService.createProjectWithContract(data),
    onSuccess: () => {
      toast.success('Projeto e contrato financeiro criados com sucesso!');
      queryClient.invalidateQueries(['client-projects', client.id]);
      queryClient.invalidateQueries(['projects']);
      queryClient.invalidateQueries(['receivables']);
      queryClient.invalidateQueries(['receivables-stats']);
      setIsAddingProject(false);
      setFormData(getInitialFormData());
      setCurrentStep(1);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao criar projeto');
    }
  });

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('T')[0].split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const d = new Date(dateString);
    return isNaN(d) ? '-' : d.toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'COMPLETED' || s === 'DELIVERED') {
      return { bg: 'bg-emerald-100 text-emerald-800', label: s === 'DELIVERED' ? 'Entregue' : 'Concluído' };
    }
    if (s === 'PRODUCTION' || s === 'ASSEMBLY' || s === 'IN_PROGRESS') {
      return { bg: 'bg-amber-100 text-amber-800', label: s === 'PRODUCTION' ? 'Produção' : (s === 'ASSEMBLY' ? 'Montagem' : 'Em Andamento') };
    }
    return { bg: 'bg-blue-100 text-blue-800', label: 'Planejamento' };
  };

  // Cálculos financeiros do wizard
  const totalAmount = parseFloat(formData.total_amount || 0);
  const entryAmount = formData.has_entry ? parseFloat(formData.entry_amount || 0) : 0;
  const remainingAmount = Math.max(0, totalAmount - entryAmount);
  const installmentsCount = parseInt(formData.installments || 1, 10);
  const baseInstallmentValue = installmentsCount > 0 ? remainingAmount / installmentsCount : 0;

  // Helpers de cartão e taxas
  const isCardPayment = (paymentType) => {
    return paymentType === 'card' || paymentType === 'credit_card' || paymentType === 'debit_card';
  };

  const getCardRate = (cardType, installments) => {
    if (!cardRates || cardRates.length === 0 || !cardType) return 0;
    const rate = cardRates.find(r =>
      r.card_brand?.toUpperCase() === cardType?.toUpperCase() &&
      r.installments === installments
    );
    return rate ? rate.rate_percentage / 100 : 0;
  };

  // Gerar divisão de parcelas com distribuição de centavos de resto na 1ª parcela
  const generateInstallmentSplits = (count, remaining, firstDate, paymentType) => {
    const numInstallments = parseInt(count, 10) || 1;
    const totalRemaining = parseFloat(remaining) || 0;
    if (numInstallments <= 0 || totalRemaining <= 0) return [];

    const totalCents = Math.round(totalRemaining * 100);
    const baseCents = Math.floor(totalCents / numInstallments);
    const remainderCents = totalCents - (baseCents * numInstallments);

    const splits = [];
    const baseDate = firstDate ? new Date(firstDate + 'T12:00:00') : new Date();

    for (let i = 0; i < numInstallments; i++) {
      const cents = baseCents + (i === 0 ? remainderCents : 0);
      const amount = cents / 100;
      const d = new Date(baseDate);
      d.setMonth(d.getMonth() + i);
      const dateStr = d.toISOString().split('T')[0];

      splits.push({
        amount: amount,
        date: dateStr,
        payment_type: paymentType || 'cash'
      });
    }
    return splits;
  };

  const handleInstallmentSplitChange = (index, field, value) => {
    setFormData(prev => {
      const newSplits = [...(prev.installment_splits || [])];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { ...prev, installment_splits: newSplits };
    });
  };

  const handleRecalculateEqualSplits = () => {
    const splits = generateInstallmentSplits(
      formData.installments,
      remainingAmount,
      formData.first_installment_date,
      formData.payment_type
    );
    setFormData(prev => ({
      ...prev,
      installment_splits: splits
    }));
    toast.success('Parcelas recalculadas com divisão exata!');
  };

  const handleAdjustLastInstallmentDifference = () => {
    const splits = [...(formData.installment_splits || [])];
    if (splits.length === 0) return;
    const currentTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    const diff = Math.round((remainingAmount - currentTotal) * 100) / 100;
    const lastIdx = splits.length - 1;
    const currentLastAmount = parseFloat(splits[lastIdx].amount) || 0;
    const newLastAmount = Math.max(0, Math.round((currentLastAmount + diff) * 100) / 100);
    splits[lastIdx] = { ...splits[lastIdx], amount: newLastAmount };
    setFormData(prev => ({ ...prev, installment_splits: splits }));
    toast.success('Diferença de centavos ajustada na última parcela!');
  };

  const handleNextStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep === 1) {
      if (!formData.project_name || formData.project_name.trim() === '') {
        toast.error('Informe o nome do projeto/ambiente');
        return;
      }
      if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
        toast.error('Informe o valor total do contrato');
        return;
      }
      if (!formData.reference_date) {
        toast.error('Informe a data de referência');
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      if (formData.has_entry) {
        if (!formData.entry_amount || parseFloat(formData.entry_amount) <= 0) {
          toast.error('Informe o valor da entrada');
          return;
        }
        if (parseFloat(formData.entry_amount) > totalAmount) {
          toast.error('O valor da entrada não pode ser maior que o total do projeto');
          return;
        }

        if (formData.entry_splits_count > 1) {
          const splitsTotal = (formData.entry_splits || []).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
          if (Math.abs(splitsTotal - parseFloat(formData.entry_amount)) > 0.05) {
            toast.error(`A soma das parcelas da entrada (${formatCurrency(splitsTotal)}) deve ser igual ao valor da entrada (${formatCurrency(parseFloat(formData.entry_amount))})`);
            return;
          }
          for (let i = 0; i < formData.entry_splits.length; i++) {
            if (!formData.entry_splits[i].date) {
              toast.error(`Informe a data da parcela ${i + 1} da entrada`);
              return;
            }
          }
        }

        if ((formData.entry_payment_type === 'pix_direct' || formData.entry_payment_type === 'cheque_direct') && !formData.entry_supplier_id) {
          toast.error('Selecione o fornecedor para o pagamento direto');
          return;
        }
      }

      // Inicializar installment_splits se ainda não existirem ou quantidade for diferente
      if (!formData.installment_splits || formData.installment_splits.length !== parseInt(formData.installments, 10)) {
        const initialSplits = generateInstallmentSplits(
          formData.installments,
          remainingAmount,
          formData.first_installment_date,
          formData.payment_type
        );
        setFormData(prev => ({ ...prev, installment_splits: initialSplits }));
      }

      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      if (formData.installments > 1 || (formData.installment_splits && formData.installment_splits.length > 0)) {
        const splits = formData.installment_splits || [];
        const splitsTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
        const diff = Math.abs(splitsTotal - remainingAmount);
        if (diff > 0.05) {
          toast.error(`A soma das parcelas (${formatCurrency(splitsTotal)}) deve ser igual ao saldo restante (${formatCurrency(remainingAmount)}). Ajuste na última parcela ou divida igualmente.`);
          return;
        }
        for (let i = 0; i < splits.length; i++) {
          if (!splits[i].date) {
            toast.error(`Informe a data de vencimento da parcela ${i + 1}`);
            return;
          }
          if (!splits[i].amount || parseFloat(splits[i].amount) <= 0) {
            toast.error(`Informe um valor válido para a parcela ${i + 1}`);
            return;
          }
        }
      }
      setCurrentStep(4);
      return;
    }
  };

  const handleSaveProject = (e) => {
    e.preventDefault();
    const payload = {
      client_id: client.id,
      project_name: formData.project_name,
      description: formData.description,
      total_amount: totalAmount,
      reference_date: formData.reference_date,
      has_entry: formData.has_entry,
      entry_amount: formData.has_entry ? entryAmount : null,
      entry_paid: formData.has_entry, // se teve entrada registrada no ato, entra paga
      entry_date: formData.has_entry ? formData.entry_date : null,
      entry_payment_type: formData.entry_payment_type,
      entry_splits_count: formData.has_entry ? (formData.entry_splits_count || 1) : 1,
      entry_splits: formData.has_entry && formData.entry_splits_count > 1 ? formData.entry_splits : [],
      entry_supplier_id: formData.has_entry && (formData.entry_payment_type === 'pix_direct' || formData.entry_payment_type === 'cheque_direct') ? formData.entry_supplier_id : null,
      payment_type: formData.payment_type,
      installments: installmentsCount,
      installment_amount: formData.installment_splits && formData.installment_splits.length > 0
        ? parseFloat(formData.installment_splits[0].amount)
        : parseFloat(baseInstallmentValue.toFixed(2)),
      first_installment_date: formData.first_installment_date,
      installment_splits: formData.installment_splits && formData.installment_splits.length > 0 ? formData.installment_splits : [],
      rate_paid_by: formData.rate_paid_by,
      card_brand: formData.card_brand || null
    };

    createProjectWithContractMutation.mutate(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto max-h-[92vh] overflow-hidden flex flex-col border border-gray-200">
        
        {/* Header do Arquivo do Cliente */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white px-5 sm:px-8 py-5 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-indigo-600/80 border border-indigo-400/40 flex items-center justify-center shadow-inner">
              <Folder className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                  Dossiê do Cliente
                </span>
                <span className="text-xs text-slate-300">ID #{client.id}</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-white mt-0.5">
                {client.apelido ? `${client.apelido} • ${client.full_name}` : client.full_name}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Ficha Resumida do Cliente */}
        <div className="bg-slate-50 px-5 sm:px-8 py-3 border-b border-gray-200 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-gray-600">
          {client.phone && (
            <span className="flex items-center gap-1.5 font-medium">
              <Phone className="w-3.5 h-3.5 text-indigo-600" />
              {client.phone}
            </span>
          )}
          {client.email && (
            <span className="flex items-center gap-1.5 font-medium">
              <Mail className="w-3.5 h-3.5 text-indigo-600" />
              {client.email}
            </span>
          )}
          {client.document_id && (
            <span className="flex items-center gap-1.5 font-medium">
              <FileText className="w-3.5 h-3.5 text-indigo-600" />
              Doc: {client.document_id}
            </span>
          )}
          {client.address && (
            <span className="flex items-center gap-1.5 font-medium truncate max-w-xs sm:max-w-md" title={client.address}>
              <MapPin className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span className="truncate">{client.address}</span>
            </span>
          )}
        </div>

        {/* KPIs de Relacionamento Financeiro */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 sm:px-8 py-4 bg-white border-b border-gray-100">
          <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100">
            <span className="text-[11px] font-semibold text-indigo-900 block">Total de Projetos</span>
            <p className="text-xl font-black text-indigo-950 mt-1">{clientStats.projectsCount}</p>
            <span className="text-[10px] text-indigo-700">Contratados na marcenaria</span>
          </div>

          <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100">
            <span className="text-[11px] font-semibold text-blue-900 block">Total em Contratos</span>
            <p className="text-xl font-black text-blue-950 mt-1 font-mono">{formatCurrency(clientStats.totalContracted)}</p>
            <span className="text-[10px] text-blue-700">Soma de todas as obras</span>
          </div>

          <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
            <span className="text-[11px] font-semibold text-emerald-900 block">Total Já Pago</span>
            <p className="text-xl font-black text-emerald-800 mt-1 font-mono">{formatCurrency(clientStats.totalReceived)}</p>
            <span className="text-[10px] text-emerald-700">Entrado no caixa</span>
          </div>

          <div className="p-3 bg-amber-50/60 rounded-xl border border-amber-100">
            <span className="text-[11px] font-semibold text-amber-900 block">Saldo a Receber</span>
            <p className="text-xl font-black text-amber-900 mt-1 font-mono">{formatCurrency(clientStats.pending)}</p>
            <span className="text-[10px] text-amber-700">Parcelas a vencer</span>
          </div>
        </div>

        {/* Barra de Ações & Abas */}
        <div className="px-5 sm:px-8 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 bg-gray-200/80 p-1 rounded-xl text-xs font-semibold">
            <button
              onClick={() => setTab('active')}
              className={`px-3.5 py-1.5 rounded-lg transition-all ${
                tab === 'active' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Em Andamento ({allProjects.filter(p => p.status !== 'COMPLETED' && p.status !== 'DELIVERED').length})
            </button>
            <button
              onClick={() => setTab('completed')}
              className={`px-3.5 py-1.5 rounded-lg transition-all ${
                tab === 'completed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Concluídos ({allProjects.filter(p => p.status === 'COMPLETED' || p.status === 'DELIVERED').length})
            </button>
            <button
              onClick={() => setTab('all')}
              className={`px-3.5 py-1.5 rounded-lg transition-all ${
                tab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Todos ({allProjects.length})
            </button>
          </div>

          {!isAddingProject && (
            <button
              onClick={() => {
                setIsAddingProject(true);
                setCurrentStep(1);
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar Projeto</span>
            </button>
          )}
        </div>

        {/* Área de Conteúdo */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-6">

          {/* O WIZARD: Adicionar Projeto com Contrato */}
          {isAddingProject && (
            <div className="bg-white rounded-2xl border-2 border-emerald-500 shadow-lg p-5 sm:p-7 space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    Novo Projeto do Cliente
                  </span>
                  <h3 className="text-xl font-black text-gray-900 mt-1">
                    Adicionar Projeto com Contrato Financeiro
                  </h3>
                  <p className="text-xs text-gray-500">
                    O projeto será criado para este cliente com código oficial e todas as parcelas cairão no financeiro.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddingProject(false)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Indicador de Passos */}
              <div className="grid grid-cols-4 gap-2 text-center text-xs font-semibold">
                <div className={`p-2 rounded-lg border ${currentStep >= 1 ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-gray-50 text-gray-400'}`}>
                  1. Projeto & Valor
                </div>
                <div className={`p-2 rounded-lg border ${currentStep >= 2 ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-gray-50 text-gray-400'}`}>
                  2. Entrada (À Vista)
                </div>
                <div className={`p-2 rounded-lg border ${currentStep >= 3 ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-gray-50 text-gray-400'}`}>
                  3. Parcelamento
                </div>
                <div className={`p-2 rounded-lg border ${currentStep >= 4 ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-gray-50 text-gray-400'}`}>
                  4. Confirmação
                </div>
              </div>

              {/* PASSO 1: Identificação e Valor */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Nome do Projeto / Ambiente *
                      </label>
                      <input
                        type="text"
                        value={formData.project_name}
                        onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                        placeholder="Ex: Cozinha Planejada e Área Gourmet"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Valor Total do Contrato (R$) *
                      </label>
                      <CurrencyInput
                        name="total_amount"
                        value={formData.total_amount}
                        onChange={(val) => setFormData({ ...formData, total_amount: val })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono font-bold focus:ring-2 focus:ring-emerald-500"
                        placeholder="35000"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Data de Referência do Contrato *
                      </label>
                      <input
                        type="date"
                        value={formData.reference_date}
                        onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Descrição / Detalhes do Ambiente
                      </label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Ex: Armários em MDF Louro Freijó com perfil de alumínio"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-all shadow-sm"
                    >
                      <span>Avançar para Entrada</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 2: Entrada */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                    <div>
                      <span className="font-bold text-gray-900 text-sm block">Teve Entrada? / Nova Entrada?</span>
                      <span className="text-xs text-gray-500">Se sim, você pode definir entrada à vista ou fracionar em até 10 parcelas.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.has_entry}
                      onChange={(e) => setFormData({ ...formData, has_entry: e.target.checked })}
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>

                  {formData.has_entry && (
                    <div className="space-y-4">
                      {/* Seletor de número de parcelas da entrada */}
                      <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                        <label className="font-bold text-amber-950">
                          Fracionar entrada em:
                        </label>
                        <select
                          value={formData.entry_splits_count || 1}
                          onChange={(e) => handleEntrySplitsCountChange(e.target.value)}
                          className="px-3 py-1.5 border border-amber-300 rounded-lg text-sm bg-white font-medium focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="1">1 parcela (À vista)</option>
                          <option value="2">2 parcelas</option>
                          <option value="3">3 parcelas</option>
                          <option value="4">4 parcelas</option>
                          <option value="5">5 parcelas</option>
                          <option value="6">6 parcelas</option>
                          <option value="7">7 parcelas</option>
                          <option value="8">8 parcelas</option>
                          <option value="9">9 parcelas</option>
                          <option value="10">10 parcelas</option>
                        </select>
                      </div>

                      {/* Se for 1 parcela, dados simples de entrada */}
                      {(!formData.entry_splits_count || formData.entry_splits_count === 1) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-emerald-50/50 rounded-xl border border-emerald-200">
                          <div>
                            <label className="block text-xs font-bold text-emerald-950 mb-1">Valor da Entrada (R$) *</label>
                            <CurrencyInput
                              name="entry_amount"
                              value={formData.entry_amount}
                              onChange={(val) => setFormData({ ...formData, entry_amount: val })}
                              className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-sm font-mono font-bold"
                              placeholder="5000"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-emerald-950 mb-1">Data do Pagamento *</label>
                            <input
                              type="date"
                              value={formData.entry_date}
                              onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                              className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-emerald-950 mb-1">Forma de Pagamento *</label>
                            <select
                              value={formData.entry_payment_type}
                              onChange={(e) => setFormData({ ...formData, entry_payment_type: e.target.value })}
                              className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-sm bg-white"
                            >
                              <option value="cash">Dinheiro</option>
                              <option value="pix">PIX</option>
                              <option value="pix_direct">PIX direto ao fornecedor</option>
                              <option value="card">Cartão</option>
                              <option value="bank_transfer">Transferência Bancária</option>
                              <option value="cheque">Cheque</option>
                              <option value="cheque_direct">Cheque direto ao fornecedor</option>
                              <option value="boleto">Boleto</option>
                            </select>
                          </div>

                          {(formData.entry_payment_type === 'pix_direct' || formData.entry_payment_type === 'cheque_direct') && (
                            <div className="sm:col-span-3 mt-2 p-3 bg-amber-50 rounded-lg border border-amber-300">
                              <label className="block text-xs font-bold text-amber-900 mb-1">
                                Fornecedor (pagamento direto) *
                              </label>
                              <select
                                value={formData.entry_supplier_id || ''}
                                onChange={(e) => setFormData({ ...formData, entry_supplier_id: e.target.value })}
                                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                                required
                              >
                                <option value="">Selecione o fornecedor...</option>
                                {suppliers.map(s => (
                                  <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Se entrada for fracionada em 2+ parcelas */
                        <div className="space-y-3 p-4 bg-emerald-50/50 rounded-xl border border-emerald-200">
                          <div>
                            <label className="block text-xs font-bold text-emerald-950 mb-1">Valor Total da Entrada (R$) *</label>
                            <CurrencyInput
                              name="entry_amount"
                              value={formData.entry_amount}
                              onChange={(val) => {
                                setFormData(prev => ({ ...prev, entry_amount: val }));
                                handleEntrySplitsCountChange(formData.entry_splits_count || 2);
                              }}
                              className="w-full sm:w-64 px-3 py-2 border border-emerald-300 rounded-lg text-sm font-mono font-bold"
                              placeholder="5000"
                            />
                          </div>

                          <div className="space-y-2.5 pt-2">
                            {formData.entry_splits.map((split, index) => (
                              <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 p-3 bg-white rounded-lg border border-emerald-200 text-xs">
                                <div>
                                  <label className="block font-medium text-gray-700 mb-1">
                                    Parcela {index + 1} - Valor (R$) *
                                  </label>
                                  <CurrencyInput
                                    value={split.amount}
                                    onChange={(val) => handleEntrySplitChange(index, 'amount', val)}
                                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md font-mono"
                                    placeholder="0,00"
                                  />
                                </div>
                                <div>
                                  <label className="block font-medium text-gray-700 mb-1">
                                    Parcela {index + 1} - Data *
                                  </label>
                                  <input
                                    type="date"
                                    value={split.date}
                                    onChange={(e) => handleEntrySplitChange(index, 'date', e.target.value)}
                                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block font-medium text-gray-700 mb-1">
                                    Forma de Pagto
                                  </label>
                                  <select
                                    value={split.payment_type}
                                    onChange={(e) => handleEntrySplitChange(index, 'payment_type', e.target.value)}
                                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
                                  >
                                    <option value="cash">Dinheiro</option>
                                    <option value="pix">PIX</option>
                                    <option value="pix_direct">PIX direto ao fornecedor</option>
                                    <option value="card">Cartão</option>
                                    <option value="bank_transfer">Transferência Bancária</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="cheque_direct">Cheque direto ao fornecedor</option>
                                    <option value="boleto">Boleto</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>

                          {(() => {
                            const splitsTotal = (formData.entry_splits || []).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                            const totalEntryExpected = parseFloat(formData.entry_amount) || 0;
                            const diff = Math.round((totalEntryExpected - splitsTotal) * 100) / 100;
                            const matches = Math.abs(diff) < 0.05;

                            return (
                              <div className={`p-2.5 rounded-lg border flex items-center justify-between text-xs ${
                                matches ? 'bg-emerald-100/70 border-emerald-300 text-emerald-900 font-semibold' : 'bg-amber-100/70 border-amber-300 text-amber-900 font-semibold'
                              }`}>
                                <span>Soma das parcelas da entrada: <strong className="font-mono">{formatCurrency(splitsTotal)}</strong></span>
                                <span>{matches ? '✓ Total da entrada confere' : `Diferença de ${formatCurrency(Math.abs(diff))}`}</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between pt-2">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="px-4 py-2 border rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Voltar</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-all shadow-sm"
                    >
                      <span>Avançar para Parcelas</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 3: Parcelamento do Saldo Restante */}
              {currentStep === 3 && (() => {
                const cardRate = formData.payment_type === 'credit_card' && formData.card_brand
                  ? getCardRate(formData.card_brand, parseInt(formData.installments, 10))
                  : 0;
                const ratePercentage = cardRate * 100;
                const whoPays = formData.rate_paid_by;
                const splits = formData.installment_splits || [];
                const splitsTotal = Math.round(splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0) * 100) / 100;
                const diff = Math.round((remainingAmount - splitsTotal) * 100) / 100;
                const isSumCorrect = Math.abs(diff) <= 0.01;

                return (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-between text-xs">
                      <span>Saldo Restante a Parcelar:</span>
                      <strong className="font-mono text-sm text-blue-900">{formatCurrency(remainingAmount)}</strong>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">Número de Parcelas *</label>
                        <select
                          value={formData.installments}
                          onChange={(e) => {
                            const count = parseInt(e.target.value, 10);
                            const newSplits = generateInstallmentSplits(
                              count,
                              remainingAmount,
                              formData.first_installment_date,
                              formData.payment_type
                            );
                            setFormData({
                              ...formData,
                              installments: count,
                              installment_splits: newSplits
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        >
                          {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n}>
                              {n === 1 ? '1x (À vista do restante)' : `${n}x parcelas`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">Vencimento da 1ª Parcela *</label>
                        <input
                          type="date"
                          value={formData.first_installment_date}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            const newSplits = generateInstallmentSplits(
                              formData.installments,
                              remainingAmount,
                              newDate,
                              formData.payment_type
                            );
                            setFormData({
                              ...formData,
                              first_installment_date: newDate,
                              installment_splits: newSplits
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">Forma de Pagamento Padrão *</label>
                        <select
                          value={formData.payment_type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const updatedSplits = (formData.installment_splits || []).map(s => ({
                              ...s,
                              payment_type: newType
                            }));
                            setFormData({
                              ...formData,
                              payment_type: newType,
                              installment_splits: updatedSplits
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        >
                          <option value="boleto">Boleto Bancário</option>
                          <option value="pix">PIX Agendado</option>
                          <option value="cheque">Cheque Pré-datado</option>
                          <option value="credit_card">Cartão de Crédito</option>
                          <option value="bank_transfer">Transferência Bancária</option>
                          <option value="cash">Dinheiro em Espécie</option>
                        </select>
                      </div>
                    </div>

                    {/* Configurações de Cartão de Crédito com Simulação e Taxas */}
                    {formData.payment_type === 'credit_card' && (
                      <div className="p-4 bg-amber-50/80 rounded-xl border border-amber-200 space-y-3 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block font-bold text-amber-950 mb-1">Bandeira do Cartão</label>
                            <select
                              value={formData.card_brand}
                              onChange={(e) => setFormData({ ...formData, card_brand: e.target.value })}
                              className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                            >
                              <option value="">Selecione...</option>
                              <option value="VISA">Visa</option>
                              <option value="MASTERCARD">Mastercard</option>
                              <option value="ELO">Elo</option>
                              <option value="HIPERCARD">Hipercard</option>
                            </select>
                          </div>

                          <div>
                            <label className="block font-bold text-amber-950 mb-1">Quem assume a taxa?</label>
                            <select
                              value={formData.rate_paid_by}
                              onChange={(e) => setFormData({ ...formData, rate_paid_by: e.target.value })}
                              className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                            >
                              <option value="cliente">Cliente (Repassar juros no valor)</option>
                              <option value="empresa">Empresa (Absorver custo da taxa)</option>
                            </select>
                          </div>
                        </div>

                        {/* Simulação da Taxa da Operadora */}
                        {formData.card_brand && (
                          <div className="p-3 bg-white rounded-lg border border-amber-200 space-y-1.5">
                            <div className="flex items-center justify-between font-bold text-amber-950">
                              <span>Taxa da Operadora ({formData.card_brand} em {formData.installments}x):</span>
                              <span className="font-mono text-sm">{ratePercentage.toFixed(2)}%</span>
                            </div>

                            {cardRate > 0 ? (
                              whoPays === 'cliente' ? (
                                <div className="space-y-1 text-gray-700 pt-1 border-t border-amber-100">
                                  <div className="flex justify-between">
                                    <span>Total de juros repassados:</span>
                                    <strong className="text-amber-800 font-mono">+{formatCurrency(remainingAmount * cardRate)}</strong>
                                  </div>
                                  <div className="flex justify-between text-emerald-800 font-bold">
                                    <span>Valor total a passar na maquininha:</span>
                                    <span className="font-mono">{formatCurrency(remainingAmount * (1 + cardRate))}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-gray-500">
                                    <span>Valor na maquininha por parcela:</span>
                                    <span className="font-mono">{formatCurrency((remainingAmount * (1 + cardRate)) / (parseInt(formData.installments, 10) || 1))}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="pt-1 border-t border-amber-100 text-purple-700 font-medium">
                                  * A empresa absorverá {formatCurrency(remainingAmount * cardRate)} de taxa na liquidação bancária.
                                </div>
                              )
                            ) : (
                              <p className="text-gray-500 italic text-[11px]">Nenhuma taxa encontrada para esta combinação (0%).</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tabela de Parcelas Individuais (Personalização & Ajuste Fino de Centavos) */}
                    {splits.length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800 uppercase tracking-wider">
                            <Calculator className="w-4 h-4 text-blue-600" />
                            <span>Detalhamento e Vencimento das Parcelas ({splits.length})</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRecalculateEqualSplits}
                            className="px-2.5 py-1 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-xs font-bold text-blue-700 flex items-center gap-1 shadow-2xs"
                            title="Distribuir saldo restante igualmente entre as parcelas com centavos na 1ª parcela"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            <span>Dividir Igualmente</span>
                          </button>
                        </div>

                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                          {splits.map((split, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200 grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center text-xs">
                              <div className="sm:col-span-2 font-bold text-gray-800">
                                Parcela {idx + 1}/{splits.length}
                              </div>

                              <div className="sm:col-span-4">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Valor (R$) *</label>
                                <CurrencyInput
                                  value={split.amount}
                                  onChange={(val) => handleInstallmentSplitChange(idx, 'amount', val)}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md font-mono font-bold text-gray-900"
                                  placeholder="0,00"
                                />
                              </div>

                              <div className="sm:col-span-3">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Vencimento *</label>
                                <input
                                  type="date"
                                  value={split.date}
                                  onChange={(e) => handleInstallmentSplitChange(idx, 'date', e.target.value)}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-gray-800"
                                />
                              </div>

                              <div className="sm:col-span-3">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Forma Pgto</label>
                                <select
                                  value={split.payment_type}
                                  onChange={(e) => handleInstallmentSplitChange(idx, 'payment_type', e.target.value)}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white text-gray-800"
                                >
                                  <option value="boleto">Boleto</option>
                                  <option value="pix">PIX</option>
                                  <option value="cheque">Cheque</option>
                                  <option value="credit_card">Cartão Crédito</option>
                                  <option value="bank_transfer">Transferência</option>
                                  <option value="cash">Dinheiro</option>
                                </select>
                              </div>

                              {formData.payment_type === 'credit_card' && whoPays === 'cliente' && cardRate > 0 && (
                                <div className="sm:col-span-12 text-[11px] text-amber-800 bg-amber-50/60 px-2 py-1 rounded border border-amber-200 flex items-center justify-between">
                                  <span>Valor líquido: <strong>{formatCurrency(parseFloat(split.amount) || 0)}</strong></span>
                                  <span>+ Taxa {ratePercentage.toFixed(2)}%: <strong>{formatCurrency((parseFloat(split.amount) || 0) * cardRate)}</strong></span>
                                  <span>Total no Cartão: <strong className="font-mono text-emerald-800">{formatCurrency((parseFloat(split.amount) || 0) * (1 + cardRate))}</strong></span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Validação de Soma & Botão de Ajuste na Última Parcela */}
                        <div className={`p-2.5 rounded-lg border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs ${
                          isSumCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : diff > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-200 text-red-800'
                        }`}>
                          <div className="flex items-center gap-1.5 font-medium">
                            {isSumCorrect ? (
                              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                            ) : (
                              <AlertCircle className="w-4 h-4 shrink-0" />
                            )}
                            <span>
                              {isSumCorrect
                                ? `✓ Soma das parcelas confere: ${formatCurrency(splitsTotal)} (100% distribuído)`
                                : diff > 0
                                  ? `A soma das parcelas (${formatCurrency(splitsTotal)}) está abaixo do saldo restante (${formatCurrency(remainingAmount)}). Faltam ${formatCurrency(diff)}.`
                                  : `A soma das parcelas (${formatCurrency(splitsTotal)}) extrapolou o saldo restante (${formatCurrency(remainingAmount)}) em ${formatCurrency(Math.abs(diff))}!`}
                            </span>
                          </div>

                          {!isSumCorrect && (
                            <button
                              type="button"
                              onClick={handleAdjustLastInstallmentDifference}
                              className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-bold shadow-2xs whitespace-nowrap self-end sm:self-auto"
                            >
                              Ajustar na última parcela
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="px-4 py-2 border rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Voltar</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleNextStep}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-all shadow-sm"
                      >
                        <span>Avançar para Revisão</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* PASSO 4: Resumo e Confirmação */}
              {currentStep === 4 && (() => {
                const splits = formData.installment_splits || [];
                const cardRate = formData.payment_type === 'credit_card' && formData.card_brand
                  ? getCardRate(formData.card_brand, parseInt(formData.installments, 10))
                  : 0;

                return (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2.5 text-xs">
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Cliente:</span>
                        <strong className="text-gray-900">{client.full_name}</strong>
                      </div>
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Projeto / Ambiente:</span>
                        <strong className="text-gray-900">{formData.project_name}</strong>
                      </div>
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Valor Total do Contrato:</span>
                        <strong className="text-emerald-700 font-mono text-sm">{formatCurrency(totalAmount)}</strong>
                      </div>
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Entrada:</span>
                        <strong>
                          {!formData.has_entry ? (
                            'Sem entrada'
                          ) : formData.entry_splits_count > 1 ? (
                            `${formatCurrency(entryAmount)} (Fracionada em ${formData.entry_splits_count}x)`
                          ) : (
                            `${formatCurrency(entryAmount)} (${getPaymentMethodLabel(formData.entry_payment_type)}${
                              (formData.entry_payment_type === 'pix_direct' || formData.entry_payment_type === 'cheque_direct') && formData.entry_supplier_id
                                ? ` - Fornecedor: ${suppliers.find(s => s.id === parseInt(formData.entry_supplier_id))?.name || 'Direto'}`
                                : ''
                            })`
                          )}
                        </strong>
                      </div>
                      <div className="border-b pb-2">
                        <div className="flex justify-between mb-1.5">
                          <span className="text-gray-600">Parcelamento:</span>
                          <strong>
                            {installmentsCount}x de {splits.length > 0 ? formatCurrency(parseFloat(splits[0].amount) || 0) : formatCurrency(baseInstallmentValue)} ({getPaymentMethodLabel(formData.payment_type)})
                          </strong>
                        </div>

                        {formData.payment_type === 'credit_card' && formData.card_brand && (
                          <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 text-[11px] space-y-0.5">
                            <div className="flex justify-between">
                              <span>Bandeira & Taxa:</span>
                              <strong>{formData.card_brand} ({(cardRate * 100).toFixed(2)}%)</strong>
                            </div>
                            <div className="flex justify-between">
                              <span>Responsável pela taxa:</span>
                              <strong className="capitalize">{formData.rate_paid_by}</strong>
                            </div>
                            {formData.rate_paid_by === 'cliente' && cardRate > 0 && (
                              <div className="flex justify-between text-emerald-800 font-bold">
                                <span>Total final passado no cartão:</span>
                                <strong className="font-mono">{formatCurrency(remainingAmount * (1 + cardRate))}</strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Lista resumida das parcelas */}
                      {splits.length > 1 && (
                        <div className="space-y-1 pt-1">
                          <span className="text-[11px] font-bold text-gray-700 uppercase">Cronograma de Parcelas:</span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
                            {splits.map((s, idx) => (
                              <div key={idx} className="p-1.5 bg-white rounded border border-gray-200 flex justify-between">
                                <span className="text-gray-600">{idx + 1}ª ({formatDate(s.date)}):</span>
                                <strong className="font-mono text-gray-900">{formatCurrency(parseFloat(s.amount) || 0)}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      <span>Ao salvar, o projeto oficial será gerado e todas as parcelas serão lançadas no contas a receber da empresa!</span>
                    </div>

                    <div className="flex justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        className="px-4 py-2 border rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Voltar</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProject}
                        disabled={createProjectWithContractMutation.isPending}
                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all shadow-md disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{createProjectWithContractMutation.isPending ? 'Criando Projeto...' : 'Salvar Projeto e Gerar Parcelas'}</span>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* LISTA DE PROJETOS DO CLIENTE */}
          {loadingProjects ? (
            <div className="text-center py-12 text-gray-500 text-sm">Carregando projetos do cliente...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center mx-auto">
                <DollarSign className="w-7 h-7" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">Nenhum projeto registrado neste filtro</h4>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                {tab === 'active' 
                  ? 'Este cliente não possui projetos em andamento no momento. Clique abaixo para registrar um novo projeto com contrato financeiro completo.'
                  : 'Nenhum projeto encontrado para este cliente nesta aba.'}
              </p>
              {!isAddingProject && (
                <button
                  onClick={() => {
                    setIsAddingProject(true);
                    setCurrentStep(1);
                  }}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold inline-flex items-center gap-2 shadow-sm transition-all mt-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Adicionar Projeto</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredProjects.map((project) => {
                const badge = getStatusBadge(project.status);
                const totalVal = parseFloat(project.project_value || project.estimated_budget || 0);
                const paidVal = parseFloat(project.total_received || 0);
                const progressPct = totalVal > 0 ? Math.min(100, Math.round((paidVal / totalVal) * 100)) : 0;

                return (
                  <div
                    key={project.id}
                    className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-black text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded border border-indigo-200">
                          {project.project_code || `#${project.id}`}
                        </span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${badge.bg}`}>
                          {badge.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          Criado em {formatDate(project.created_at)}
                        </span>
                      </div>

                      <h4 className="text-base font-bold text-gray-900">
                        {project.project_name || project.description || 'Projeto Sem Nome'}
                      </h4>

                      {project.description && project.project_name && (
                        <p className="text-xs text-gray-500 line-clamp-1">{project.description}</p>
                      )}

                      {/* Barra de Progresso Financeiro */}
                      <div className="pt-2 max-w-md">
                        <div className="flex items-center justify-between text-[11px] font-semibold text-gray-600 mb-1">
                          <span>Pago: <strong className="text-emerald-700 font-mono">{formatCurrency(paidVal)}</strong></span>
                          <span>Total: <strong className="text-gray-900 font-mono">{formatCurrency(totalVal)}</strong></span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${progressPct >= 100 ? 'bg-emerald-500' : 'bg-indigo-600'}`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Ações do Projeto */}
                    <div className="flex items-center gap-2 shrink-0 border-t md:border-t-0 pt-3 md:pt-0">
                      <button
                        onClick={() => setContractProject(project)}
                        className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 rounded-xl text-xs font-bold border border-indigo-200 flex items-center gap-1.5 transition-colors"
                        title="Ver ou ajustar contrato financeiro e parcelas deste projeto"
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        <span>Contrato & Parcelas</span>
                      </button>

                      <a
                        href={`/projetos/${project.id}/orcamento`}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl border transition-colors"
                        title="Ver orçamento e ficha técnica"
                      >
                        <DollarSign className="w-4 h-4" />
                      </a>

                      <a
                        href={`/projetos/${project.id}/execucao`}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl border transition-colors"
                        title="Ver execução na fábrica"
                      >
                        <Layers className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal Interno de Contrato / Saldo do Projeto Selecionado */}
      {contractProject && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto border border-gray-200">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between z-10">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                  {contractProject.project_code || `#${contractProject.id}`}
                </span>
                <h3 className="text-lg font-bold text-gray-900">
                  Contrato & Condições Financeiras — {contractProject.project_name}
                </h3>
              </div>
              <button
                onClick={() => setContractProject(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <ProjectInitialBalances
                projectId={contractProject.id}
                estimatedBudget={contractProject.project_value || contractProject.estimated_budget}
                projectName={contractProject.project_name}
                clientName={client.full_name}
                onClose={() => setContractProject(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFileModal;
